'use client';

import {
  FeedbackInviteMode,
  FeedbackInviteLocale,
  Permission,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  healthAttentionFgClass,
  Input,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@up2stay/up2promo-ui';
import { Eye, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { FeedbackComposePreview } from '@/core/components/feedback/feedback-compose-preview';
import { FeedbackComposeRecipientSources } from '@/core/components/feedback/feedback-compose-recipient-sources';
import { FeedbackFeatureGate } from '@/core/components/feedback/feedback-feature-gate';
import {
  FeedbackModeCardSelector,
  feedbackModeNeedsGoogle,
  locationHasGooglePlace,
} from '@/core/components/feedback/feedback-mode-card-selector';
import { FeedbackPageBreadcrumb } from '@/core/components/feedback/feedback-page-breadcrumb';
import { FeedbackRecipientInput } from '@/core/components/feedback/feedback-recipient-input';
import {
  FEEDBACK_GHOST_DASHED_PANEL_CLASS,
  FEEDBACK_SECTION_CARD_CLASS,
  FEEDBACK_SECTION_CARD_CONTENT_CLASS,
  FEEDBACK_SECTION_CARD_HEADER_CLASS,
  FEEDBACK_SECTION_MUTED_CLASS,
  FEEDBACK_SECTION_TITLE_CLASS,
} from '@/core/components/feedback/feedback-surface';
import { FeedbackTemplateLibraryPicker } from '@/core/components/feedback/feedback-template-library-picker';
import { WizardShell } from '@/core/components/onboarding/wizard-shell';
import { PageHeader } from '@/core/components/page-header';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  useCreateFeedbackInvite,
  useFeedbackContacts,
  useFeedbackFormTemplates,
  useFeedbackMailTemplates,
  useSendFeedbackInvite,
  useSendFeedbackInvitePreview,
  useFeedbackFeatureEnabled,
} from '@/core/hooks/use-feedback';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useQuota } from '@/core/hooks/use-quota';
import { feedbackPreviewContactName } from '@/core/lib/feedback-mail-preview';
import type { FeedbackRecipient } from '@/core/lib/parse-feedback-recipients';
import {
  workspaceBrandAccent,
  workspaceBrandLogoUrl,
} from '@/core/lib/workspace-brand';
import {
  pickDefaultMailTemplateId,
  pickDefaultTemplateId,
} from '@/core/lib/pick-default-feedback-templates';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

type PreviewTab = 'form' | 'email';

type ComposeFieldErrors = {
  location?: string;
  form?: string;
  message?: string;
  recipients?: string;
};

enum ComposeStep {
  SETUP = 1,
  RECIPIENTS = 2,
  REVIEW = 3,
}

const TOTAL_STEPS = 3;

const invalidControlClass =
  'border-destructive focus:border-destructive focus:ring-destructive/30 focus-visible:border-destructive focus-visible:ring-destructive/30';

export function FeedbackComposePage() {
  const dictRoot = useDictionary();
  const dict = dictRoot.feedback;
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canInvite = can(Permission.FEEDBACK_INVITE);
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const feedbackEnabled = useFeedbackFeatureEnabled();
  const { locations } = useLocations();
  const feedbackQuota = useQuota('quota.feedback_invite_recipients');
  const { data: formTemplates = [] } = useFeedbackFormTemplates(workspaceId);
  const { data: mailTemplates = [] } = useFeedbackMailTemplates(workspaceId);
  const { data: contacts = [] } = useFeedbackContacts(workspaceId);
  const createInvite = useCreateFeedbackInvite(workspaceId);
  const sendInvite = useSendFeedbackInvite(workspaceId);
  const previewMail = useSendFeedbackInvitePreview(workspaceId);

  const [currentStep, setCurrentStep] = useState(ComposeStep.SETUP);
  const [direction, setDirection] = useState(1);
  const [locationId, setLocationId] = useState('');
  const [formTemplateId, setFormTemplateId] = useState('');
  const [mailTemplateId, setMailTemplateId] = useState('');
  const [mode, setMode] = useState<FeedbackInviteMode>(
    FeedbackInviteMode.FEEDBACK_ONLY,
  );
  const [reviewThreshold, setReviewThreshold] = useState(4);
  const [contextLabel, setContextLabel] = useState('');
  const [recipients, setRecipients] = useState<FeedbackRecipient[]>([]);
  const [sendNow, setSendNow] = useState(true);
  const [sendAtLocal, setSendAtLocal] = useState('');
  const [previewTab, setPreviewTab] = useState<PreviewTab>('form');
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formLibraryOpen, setFormLibraryOpen] = useState(false);
  const [mailLibraryOpen, setMailLibraryOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ComposeFieldErrors>({});

  const resolvedFormTemplateId =
    formTemplateId || pickDefaultTemplateId(formTemplates);
  const resolvedMailTemplateId =
    mailTemplateId ||
    pickDefaultMailTemplateId(mailTemplates, FeedbackInviteLocale.DE);
  const resolvedLocationId =
    locationId ||
    (locations.length === 1 && locations[0] ? locations[0].id : '');
  const formTemplate = formTemplates.find(
    (t) => t.id === resolvedFormTemplateId,
  );
  const mailTemplate = mailTemplates.find(
    (t) => t.id === resolvedMailTemplateId,
  );
  const location = locations.find((l) => l.id === resolvedLocationId);
  const googleAvailable = locationHasGooglePlace(location);
  const resolvedMode =
    feedbackModeNeedsGoogle(mode) && !googleAvailable
      ? FeedbackInviteMode.FEEDBACK_ONLY
      : mode;

  const optOutEmails = useMemo(() => {
    const opted = new Set(
      contacts.filter((c) => c.feedbackMailOptOut).map((c) => c.email),
    );
    return recipients.filter((r) => opted.has(r.email)).map((r) => r.email);
  }, [contacts, recipients]);

  const included = feedbackQuota.limit ?? 0;
  const used = feedbackQuota.current;
  const remaining = feedbackQuota.remaining ?? Math.max(0, included - used);

  const previewPanel = (
    <FeedbackComposePreview
      tab={previewTab}
      onTabChange={setPreviewTab}
      formTitle={formTemplate?.title ?? dict.compose.pickFormFirst}
      contextLabel={contextLabel.trim() || null}
      questions={formTemplate?.questions ?? []}
      showComment={(formTemplate?.questions ?? []).length > 0}
      workspaceName={workspace?.name}
      locationName={location?.name}
      mailSubject={mailTemplate?.subject ?? ''}
      mailBody={mailTemplate?.body ?? ''}
      mailSample={{
        contactName: feedbackPreviewContactName(
          recipients,
          dict.preview.sampleContact,
        ),
        locationName: location?.name ?? dict.preview.sampleLocation,
        workspaceName: workspace?.name ?? 'up2promo',
      }}
      logoUrl={workspaceBrandLogoUrl(workspace?.photo)}
      accentColor={workspaceBrandAccent(formTemplate?.accentColor)}
      mailAccentColor={workspaceBrandAccent(mailTemplate?.accentColor)}
      mailCtaLabel={dict.preview.mailCta}
      poweredByLabel={dict.preview.poweredBy}
      showPoweredBy={formTemplate?.showUp2promoBranding !== false}
      labels={{
        previewForm: dict.compose.previewForm,
        previewEmail: dict.compose.previewEmail,
        overallStars: dict.preview.overallStars,
        comment: dict.preview.comment,
        commentPlaceholder: dict.preview.commentPlaceholder,
        required: dict.preview.required,
        yes: dict.preview.yes,
        no: dict.preview.no,
        scaleLow: dict.preview.scaleLow,
        scaleHigh: dict.preview.scaleHigh,
        selectPlaceholder: dict.preview.selectPlaceholder,
        submit: dict.preview.submit,
        previewNote: dict.preview.previewNote,
        from: dict.preview.from,
        to: dict.preview.to,
      }}
    />
  );

  const clearFieldError = (field: keyof ComposeFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const goToStep = (next: ComposeStep) => {
    setDirection(next > currentStep ? 1 : -1);
    setCurrentStep(next);
  };

  const validateSetup = (): boolean => {
    const nextErrors: ComposeFieldErrors = {};
    if (!resolvedLocationId) nextErrors.location = dict.compose.needLocation;
    if (!resolvedFormTemplateId) nextErrors.form = dict.compose.needForm;
    if (!resolvedMailTemplateId) nextErrors.message = dict.compose.needMessage;
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateRecipients = (): boolean => {
    if (recipients.length === 0) {
      setFieldErrors({ recipients: dict.compose.needRecipients });
      return false;
    }
    setFieldErrors((prev) => {
      if (!prev.recipients) return prev;
      const next = { ...prev };
      delete next.recipients;
      return next;
    });
    return true;
  };

  const onSubmit = async () => {
    if (!canInvite || !workspaceId) return;
    if (!validateSetup() || !validateRecipients()) return;

    setSubmitting(true);
    try {
      const sendAt =
        !sendNow && sendAtLocal
          ? new Date(sendAtLocal).toISOString()
          : undefined;
      const created = await createInvite.mutateAsync({
        locationId: resolvedLocationId,
        mode: resolvedMode,
        reviewThreshold:
          resolvedMode === FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW
            ? reviewThreshold
            : undefined,
        contextLabel: contextLabel.trim() || null,
        formTemplateId: resolvedFormTemplateId,
        mailTemplateId: resolvedMailTemplateId,
        sendAt: sendAt ?? null,
        recipients,
      });
      if (!created?.id) throw new Error('missing invite');
      await sendInvite.mutateAsync(created.id);
      toast.success(
        sendNow ? dict.compose.sendSuccess : dict.compose.scheduleSuccess,
      );
      navigateShell(shellPaths.feedbackInviteDetail(created.id));
    } catch {
      toast.error(dict.compose.sendFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === ComposeStep.SETUP) {
      if (!validateSetup()) return;
      goToStep(ComposeStep.RECIPIENTS);
      return;
    }
    if (currentStep === ComposeStep.RECIPIENTS) {
      if (!validateRecipients()) return;
      goToStep(ComposeStep.REVIEW);
      return;
    }
    void onSubmit();
  };

  const handleTestSend = async () => {
    if (
      !resolvedLocationId ||
      !resolvedFormTemplateId ||
      !resolvedMailTemplateId
    ) {
      toast.error(dict.compose.testSendNeedSetup);
      return;
    }
    try {
      const result = await previewMail.mutateAsync({
        locationId: resolvedLocationId,
        formTemplateId: resolvedFormTemplateId,
        mailTemplateId: resolvedMailTemplateId,
      });
      toast.success(
        dict.compose.testSendSuccess.replace('{email}', result.sentTo),
      );
    } catch {
      toast.error(dict.compose.testSendFailed);
    }
  };

  if (!canInvite) {
    return (
      <AppIonPage>
        <AppPageContent>
          <AppPageLayout>
            <PageHeader
              title={dict.compose.title}
              breadcrumb={
                <FeedbackPageBreadcrumb
                  domainLabel={dictRoot.nav.navDomainWork}
                  pageLabel={dictRoot.nav.feedback}
                />
              }
              backHref={shellPaths.feedbackHome}
            />
            <p className={FEEDBACK_SECTION_MUTED_CLASS}>
              {dict.compose.noPermission}
            </p>
          </AppPageLayout>
        </AppPageContent>
      </AppIonPage>
    );
  }

  const sendLabel = sendNow ? dict.compose.send : dict.compose.schedule;
  const stepLabels = [
    dict.compose.stepSetup,
    dict.compose.stepRecipients,
    dict.compose.stepReview,
  ];
  const nextLabel =
    currentStep === ComposeStep.REVIEW ? sendLabel : dict.compose.stepNext;
  const modeLabels = {
    feedbackOnly: dict.modes.feedbackOnly,
    feedbackOnlyDescription: dict.modes.feedbackOnlyDescription,
    feedbackThenReview: dict.modes.feedbackThenReview,
    feedbackThenReviewDescription: dict.modes.feedbackThenReviewDescription,
    directReview: dict.modes.directReview,
    directReviewDescription: dict.modes.directReviewDescription,
    needsLocation: dict.compose.modeNeedsLocation,
    needsGoogle: dict.compose.modeNeedsGoogle,
  };

  const summaryRow = (label: string, value: string) => (
    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 text-sm text-foreground">{value}</span>
    </div>
  );

  const modeSummary =
    resolvedMode === FeedbackInviteMode.DIRECT_REVIEW
      ? dict.modes.directReview
      : resolvedMode === FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW
        ? dict.modes.feedbackThenReview
        : dict.modes.feedbackOnly;

  return (
    <AppIonPage>
      <AppPageContent scrollY={false}>
        <div className="relative flex h-full min-h-0 flex-col">
          <AppPageLayout
            withGap={false}
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden"
          >
            <PageHeader
              title={dict.compose.title}
              breadcrumb={
                <FeedbackPageBreadcrumb
                  domainLabel={dictRoot.nav.navDomainWork}
                  pageLabel={dictRoot.nav.feedback}
                />
              }
              backHref={
                currentStep === ComposeStep.SETUP
                  ? shellPaths.feedbackHome
                  : undefined
              }
              onBack={
                currentStep === ComposeStep.SETUP
                  ? undefined
                  : () => goToStep((currentStep - 1) as ComposeStep)
              }
              actions={
                // Locked plan: nothing to save or preview (DEV-549).
                feedbackEnabled &&
                (isMobile ? (
                  <Button
                    type="button"
                    variant="ghostAccent"
                    size="sm"
                    onClick={() => setMobilePreviewOpen(true)}
                  >
                    <Eye className="size-4" />
                    {dict.compose.preview}
                  </Button>
                ) : undefined)
              }
            />

            <FeedbackFeatureGate>
              <WizardShell
                currentStep={currentStep}
                totalSteps={TOTAL_STEPS}
                stepLabels={stepLabels}
                direction={direction}
                stepKey={currentStep}
                onBack={
                  currentStep > ComposeStep.SETUP
                    ? () => goToStep((currentStep - 1) as ComposeStep)
                    : undefined
                }
                onNext={handleNext}
                nextLabel={nextLabel}
                nextDisabled={submitting}
                isLoading={submitting}
                showBack={false}
                variant="shell"
                contentAlignment="start"
                contentClassName="max-w-5xl"
              >
                <div className="grid min-w-0 gap-6 md:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.9fr)]">
                  <div className="flex min-w-0 flex-col gap-4">
                    {currentStep === ComposeStep.SETUP ? (
                      <>
                        <Card
                          className={cn(
                            'min-w-0 w-full',
                            FEEDBACK_SECTION_CARD_CLASS,
                          )}
                        >
                          <CardHeader
                            className={cn(
                              'min-w-0',
                              FEEDBACK_SECTION_CARD_HEADER_CLASS,
                            )}
                          >
                            <CardTitle className={FEEDBACK_SECTION_TITLE_CLASS}>
                              {dict.compose.whoWhere}
                            </CardTitle>
                          </CardHeader>
                          <CardContent
                            className={cn(
                              'min-w-0 space-y-4',
                              FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                            )}
                          >
                            <div className="min-w-0 space-y-2">
                              <div className="flex items-center gap-0.5">
                                <Label>{dict.compose.location}</Label>
                                <SettingsInfoHint label={dict.compose.location}>
                                  {dict.compose.locationRequiredHint}
                                </SettingsInfoHint>
                              </div>
                              <Select
                                value={resolvedLocationId}
                                onValueChange={(value) => {
                                  setLocationId(value);
                                  clearFieldError('location');
                                }}
                              >
                                <SelectTrigger
                                  aria-invalid={
                                    fieldErrors.location ? true : undefined
                                  }
                                  className={cn(
                                    fieldErrors.location && invalidControlClass,
                                  )}
                                >
                                  <SelectValue
                                    placeholder={
                                      dict.compose.locationPlaceholder
                                    }
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {locations.map((loc) => (
                                    <SelectItem key={loc.id} value={loc.id}>
                                      {loc.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {fieldErrors.location ? (
                                <p
                                  className="text-xs text-destructive"
                                  role="alert"
                                >
                                  {fieldErrors.location}
                                </p>
                              ) : null}
                            </div>
                            <div className="min-w-0 space-y-2">
                              <div className="flex items-center gap-0.5">
                                <Label>{dict.compose.contextLabel}</Label>
                                <SettingsInfoHint
                                  label={dict.compose.contextLabel}
                                >
                                  {dict.compose.contextHelp}
                                </SettingsInfoHint>
                              </div>
                              <Input
                                value={contextLabel}
                                onChange={(e) =>
                                  setContextLabel(e.target.value)
                                }
                                placeholder={dict.compose.contextPlaceholder}
                                className="min-w-0 w-full"
                              />
                            </div>
                          </CardContent>
                        </Card>

                        <Card
                          className={cn(
                            'min-w-0 w-full',
                            FEEDBACK_SECTION_CARD_CLASS,
                          )}
                        >
                          <CardHeader
                            className={cn(
                              'min-w-0',
                              FEEDBACK_SECTION_CARD_HEADER_CLASS,
                            )}
                          >
                            <div className="flex items-center gap-0.5">
                              <CardTitle
                                className={FEEDBACK_SECTION_TITLE_CLASS}
                              >
                                {dict.compose.pickKits}
                              </CardTitle>
                              {formTemplates.length > 0 &&
                              mailTemplates.length > 0 ? (
                                <SettingsInfoHint label={dict.compose.pickKits}>
                                  {dict.compose.defaultsHint}
                                </SettingsInfoHint>
                              ) : null}
                            </div>
                          </CardHeader>
                          <CardContent
                            className={cn(
                              'min-w-0 space-y-4',
                              FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                            )}
                          >
                            <div className="min-w-0 space-y-2">
                              <Label>{dict.compose.form}</Label>
                              {formTemplates.length === 0 ? (
                                <div
                                  className={cn(
                                    FEEDBACK_GHOST_DASHED_PANEL_CLASS,
                                    'px-3 py-3',
                                    fieldErrors.form &&
                                      'border-destructive/70 bg-destructive/5',
                                  )}
                                >
                                  <p className="text-sm text-muted-foreground">
                                    {dict.compose.noFormsYet}
                                  </p>
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="mt-2"
                                    onClick={() => setFormLibraryOpen(true)}
                                  >
                                    {dict.forms.fromLibrary}
                                  </Button>
                                </div>
                              ) : (
                                <Select
                                  value={resolvedFormTemplateId}
                                  onValueChange={(value) => {
                                    setFormTemplateId(value);
                                    clearFieldError('form');
                                  }}
                                >
                                  <SelectTrigger
                                    aria-invalid={
                                      fieldErrors.form ? true : undefined
                                    }
                                    className={cn(
                                      fieldErrors.form && invalidControlClass,
                                    )}
                                  >
                                    <SelectValue
                                      placeholder={dict.compose.formPlaceholder}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {formTemplates.map((t) => (
                                      <SelectItem key={t.id} value={t.id}>
                                        {t.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {fieldErrors.form ? (
                                <p
                                  className="text-xs text-destructive"
                                  role="alert"
                                >
                                  {fieldErrors.form}
                                </p>
                              ) : formTemplate?.description ? (
                                <p className="text-xs text-muted-foreground">
                                  {formTemplate.description}
                                </p>
                              ) : null}
                            </div>
                            <div className="min-w-0 space-y-2">
                              <Label>{dict.compose.message}</Label>
                              {mailTemplates.length === 0 ? (
                                <div
                                  className={cn(
                                    FEEDBACK_GHOST_DASHED_PANEL_CLASS,
                                    'px-3 py-3',
                                    fieldErrors.message &&
                                      'border-destructive/70 bg-destructive/5',
                                  )}
                                >
                                  <p className="text-sm text-muted-foreground">
                                    {dict.compose.noMessagesYet}
                                  </p>
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="mt-2"
                                    onClick={() => setMailLibraryOpen(true)}
                                  >
                                    {dict.messages.fromLibrary}
                                  </Button>
                                </div>
                              ) : (
                                <Select
                                  value={resolvedMailTemplateId}
                                  onValueChange={(value) => {
                                    setMailTemplateId(value);
                                    clearFieldError('message');
                                  }}
                                >
                                  <SelectTrigger
                                    aria-invalid={
                                      fieldErrors.message ? true : undefined
                                    }
                                    className={cn(
                                      fieldErrors.message &&
                                        invalidControlClass,
                                    )}
                                  >
                                    <SelectValue
                                      placeholder={
                                        dict.compose.messagePlaceholder
                                      }
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {mailTemplates.map((t) => (
                                      <SelectItem key={t.id} value={t.id}>
                                        {t.name}
                                        {t.locale === FeedbackInviteLocale.DE
                                          ? ' · DE'
                                          : ' · EN'}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {fieldErrors.message ? (
                                <p
                                  className="text-xs text-destructive"
                                  role="alert"
                                >
                                  {fieldErrors.message}
                                </p>
                              ) : mailTemplate?.description ? (
                                <p className="text-xs text-muted-foreground">
                                  {mailTemplate.description}
                                </p>
                              ) : null}
                            </div>
                          </CardContent>
                        </Card>

                        <Card
                          className={cn(
                            'min-w-0 w-full',
                            FEEDBACK_SECTION_CARD_CLASS,
                          )}
                        >
                          <CardHeader
                            className={cn(
                              'min-w-0',
                              FEEDBACK_SECTION_CARD_HEADER_CLASS,
                            )}
                          >
                            <CardTitle className={FEEDBACK_SECTION_TITLE_CLASS}>
                              {dict.compose.goal}
                            </CardTitle>
                          </CardHeader>
                          <CardContent
                            className={cn(
                              'min-w-0 space-y-4',
                              FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                            )}
                          >
                            <FeedbackModeCardSelector
                              value={resolvedMode}
                              onValueChange={setMode}
                              googleAvailable={googleAvailable}
                              hasLocation={Boolean(resolvedLocationId)}
                              labels={modeLabels}
                            />
                            {resolvedMode ===
                            FeedbackInviteMode.FEEDBACK_THEN_OPTIONAL_REVIEW ? (
                              <div className="min-w-0 space-y-2">
                                <div className="flex items-center gap-0.5">
                                  <Label>{dict.compose.threshold}</Label>
                                  <SettingsInfoHint
                                    label={dict.compose.threshold}
                                  >
                                    {dict.compose.thresholdHelp}
                                  </SettingsInfoHint>
                                </div>
                                <Input
                                  type="number"
                                  min={1}
                                  max={5}
                                  value={reviewThreshold}
                                  onChange={(e) =>
                                    setReviewThreshold(
                                      Number(e.target.value) || 4,
                                    )
                                  }
                                  className="min-w-0 w-full"
                                />
                              </div>
                            ) : null}
                          </CardContent>
                        </Card>
                      </>
                    ) : null}

                    {currentStep === ComposeStep.RECIPIENTS ? (
                      <Card
                        className={cn(
                          'min-w-0 w-full',
                          FEEDBACK_SECTION_CARD_CLASS,
                        )}
                      >
                        <CardHeader
                          className={cn(
                            'min-w-0',
                            FEEDBACK_SECTION_CARD_HEADER_CLASS,
                          )}
                        >
                          <CardTitle className={FEEDBACK_SECTION_TITLE_CLASS}>
                            {dict.compose.recipients}
                          </CardTitle>
                        </CardHeader>
                        <CardContent
                          className={cn(
                            'min-w-0 space-y-4',
                            FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                          )}
                        >
                          <FeedbackComposeRecipientSources
                            workspaceId={workspaceId}
                            recipients={recipients}
                            remainingSlots={
                              feedbackQuota.isUnlimited
                                ? null
                                : Math.max(0, remaining - recipients.length)
                            }
                            onRecipientsChange={(next) => {
                              setRecipients(next);
                              if (next.length > 0)
                                clearFieldError('recipients');
                            }}
                            contacts={contacts}
                            labels={{
                              fromLeads: dict.compose.fromLeads,
                              fromLeadsTitle: dict.compose.fromLeadsTitle,
                              fromLeadsSearch: dict.compose.fromLeadsSearch,
                              fromLeadsEmpty: dict.compose.fromLeadsEmpty,
                              fromLeadsAdd: dict.compose.fromLeadsAdd,
                              fromLeadsAddSelected:
                                dict.compose.fromLeadsAddSelected,
                              fromContacts: dict.compose.fromContacts,
                              fromCsv: dict.compose.fromCsv,
                              fromCsvHint: dict.compose.fromCsvHint,
                              fromCsvExample: dict.compose.fromCsvExample,
                              importPreviewTitle:
                                dict.compose.importPreviewTitle,
                              importPreviewConfirm:
                                dict.compose.importPreviewConfirm,
                              importPreviewCancel:
                                dict.compose.importPreviewCancel,
                              importPreviewReady:
                                dict.compose.importPreviewReady,
                              importPreviewInvalid:
                                dict.compose.importPreviewInvalid,
                              importPreviewDuplicate:
                                dict.compose.importPreviewDuplicate,
                              importPreviewOverQuota:
                                dict.compose.importPreviewOverQuota,
                              noneReady: dict.compose.importNoneReady,
                            }}
                            onImported={(added) => {
                              if (added > 0) {
                                toast.success(
                                  dict.compose.fromCsvImported.replace(
                                    '{count}',
                                    String(added),
                                  ),
                                );
                              }
                            }}
                          />
                          <FeedbackRecipientInput
                            recipients={recipients}
                            remainingSlots={
                              feedbackQuota.isUnlimited
                                ? null
                                : Math.max(0, remaining - recipients.length)
                            }
                            onRecipientsChange={(next) => {
                              setRecipients(next);
                              if (next.length > 0)
                                clearFieldError('recipients');
                            }}
                            error={fieldErrors.recipients}
                            labels={{
                              name: dict.compose.recipientName,
                              namePlaceholder:
                                dict.compose.recipientNamePlaceholder,
                              email: dict.compose.recipientEmail,
                              emailPlaceholder:
                                dict.compose.recipientEmailPlaceholder,
                              add: dict.compose.addRecipient,
                              empty: dict.compose.recipientsEmpty,
                              count: dict.compose.recipientsCount,
                              invalidEmail: dict.compose.invalidEmail,
                              duplicateEmail: dict.compose.duplicateEmail,
                              remove: dict.compose.removeRecipient,
                              pasteHint: dict.compose.recipientsPasteHint,
                              importPreviewTitle:
                                dict.compose.importPreviewTitle,
                              importPreviewConfirm:
                                dict.compose.importPreviewConfirm,
                              importPreviewCancel:
                                dict.compose.importPreviewCancel,
                              importPreviewReady:
                                dict.compose.importPreviewReady,
                              importPreviewInvalid:
                                dict.compose.importPreviewInvalid,
                              importPreviewDuplicate:
                                dict.compose.importPreviewDuplicate,
                              importPreviewOverQuota:
                                dict.compose.importPreviewOverQuota,
                              noneReady: dict.compose.importNoneReady,
                            }}
                          />
                          {optOutEmails.length > 0 ? (
                            <p
                              className={cn(
                                'text-[13.5px] leading-relaxed',
                                healthAttentionFgClass,
                              )}
                            >
                              {dict.compose.optOutWarning.replace(
                                '{emails}',
                                optOutEmails.join(', '),
                              )}
                            </p>
                          ) : null}
                          {included > 0 ? (
                            <p className="text-xs text-muted-foreground">
                              {dict.compose.quotaUsage
                                .replace('{count}', String(recipients.length))
                                .replace('{remaining}', String(remaining))
                                .replace('{used}', String(used))
                                .replace('{included}', String(included))}
                            </p>
                          ) : null}
                        </CardContent>
                      </Card>
                    ) : null}

                    {currentStep === ComposeStep.REVIEW ? (
                      <>
                        <Card
                          className={cn(
                            'min-w-0 w-full',
                            FEEDBACK_SECTION_CARD_CLASS,
                          )}
                        >
                          <CardHeader
                            className={cn(
                              'min-w-0',
                              FEEDBACK_SECTION_CARD_HEADER_CLASS,
                            )}
                          >
                            <CardTitle className={FEEDBACK_SECTION_TITLE_CLASS}>
                              {dict.compose.stepReview}
                            </CardTitle>
                          </CardHeader>
                          <CardContent
                            className={cn(
                              'min-w-0 space-y-3',
                              FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                            )}
                          >
                            {summaryRow(
                              dict.compose.reviewLocation,
                              location?.name ?? dict.compose.reviewMissing,
                            )}
                            {summaryRow(
                              dict.compose.reviewForm,
                              formTemplate?.name ?? dict.compose.reviewMissing,
                            )}
                            {summaryRow(
                              dict.compose.reviewMessage,
                              mailTemplate?.name ?? dict.compose.reviewMissing,
                            )}
                            {summaryRow(dict.compose.reviewGoal, modeSummary)}
                            {summaryRow(
                              dict.compose.reviewContext,
                              contextLabel.trim() || dict.compose.reviewMissing,
                            )}
                            {summaryRow(
                              dict.compose.reviewRecipients,
                              dict.compose.recipientsCount.replace(
                                '{count}',
                                String(recipients.length),
                              ),
                            )}
                          </CardContent>
                        </Card>

                        <Card
                          className={cn(
                            'min-w-0 w-full',
                            FEEDBACK_SECTION_CARD_CLASS,
                          )}
                        >
                          <CardHeader
                            className={cn(
                              'min-w-0',
                              FEEDBACK_SECTION_CARD_HEADER_CLASS,
                            )}
                          >
                            <CardTitle className={FEEDBACK_SECTION_TITLE_CLASS}>
                              {dict.compose.when}
                            </CardTitle>
                          </CardHeader>
                          <CardContent
                            className={cn(
                              'min-w-0 space-y-4',
                              FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                            )}
                          >
                            <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2">
                              <Label htmlFor="send-now">
                                {dict.compose.sendNow}
                              </Label>
                              <Switch
                                id="send-now"
                                checked={sendNow}
                                onCheckedChange={setSendNow}
                              />
                            </div>
                            {!sendNow ? (
                              <div className="min-w-0 space-y-2">
                                <Label>{dict.compose.sendAt}</Label>
                                <Input
                                  type="datetime-local"
                                  value={sendAtLocal}
                                  onChange={(e) =>
                                    setSendAtLocal(e.target.value)
                                  }
                                  className="min-w-0 w-full"
                                />
                              </div>
                            ) : null}
                            {mailTemplate &&
                            mailTemplate.reminders.length > 0 ? (
                              <p className="text-xs text-muted-foreground">
                                {dict.compose.remindersFromMessage.replace(
                                  '{count}',
                                  String(mailTemplate.reminders.length),
                                )}
                              </p>
                            ) : null}
                            <Button
                              type="button"
                              variant="outline"
                              disabled={previewMail.isPending}
                              isLoading={previewMail.isPending}
                              onClick={() => void handleTestSend()}
                            >
                              <Send className="size-4" />
                              {dict.compose.testSend}
                            </Button>
                          </CardContent>
                        </Card>
                      </>
                    ) : null}
                  </div>

                  <aside className="hidden min-w-0 md:block">
                    {previewPanel}
                  </aside>
                </div>
              </WizardShell>
            </FeedbackFeatureGate>
          </AppPageLayout>
        </div>

        <ResponsiveDialog
          open={mobilePreviewOpen}
          onOpenChange={setMobilePreviewOpen}
        >
          <ResponsiveDialogContent scrollable={false}>
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle>
                {dict.compose.preview}
              </ResponsiveDialogTitle>
            </ResponsiveDialogHeader>
            <div className="px-1 pb-4">{previewPanel}</div>
          </ResponsiveDialogContent>
        </ResponsiveDialog>

        <FeedbackTemplateLibraryPicker
          open={formLibraryOpen}
          onOpenChange={setFormLibraryOpen}
          kind="form"
          onAdded={(templateId) => {
            setFormTemplateId(templateId);
            clearFieldError('form');
          }}
        />
        <FeedbackTemplateLibraryPicker
          open={mailLibraryOpen}
          onOpenChange={setMailLibraryOpen}
          kind="mail"
          onAdded={(templateId) => {
            setMailTemplateId(templateId);
            clearFieldError('message');
          }}
        />
      </AppPageContent>
    </AppIonPage>
  );
}
